<template>
  <div v-if="showModal" class="modal-container" @click.self="closeModal">
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40"></div>

    <div class="modal-content bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-50 relative">
      <!-- Close Modal Icon Button -->
      <button 
        @click="closeModal" 
        class="absolute top-2 right-4 bg-white rounded-full shadow-md p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Close Modal"
      >
        <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-900">Project Settings</h2>
      </div>

      <!-- Content -->
      <div class="px-6 py-6 space-y-8">
        <!-- Visibility Section -->
        <div class="flex items-center space-x-8"> <!-- Increased space between label and select box -->
          <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Visibility</label>
          <div class="relative">
            <select 
              v-model="visibility" 
              class="appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
            <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Project Logo Section -->
        <div class="space-y-2"> <!-- Adjusted padding between components -->
          <div class="flex items-start justify-between">
            <!-- Label and Description -->
            <div>
              <h3 class="text-sm font-medium text-gray-700 mb-1">Project Logo</h3>
              <p class="text-sm text-gray-500 max-w-[200px]">This will be displayed on the project public page.</p> <!-- Increased width -->
            </div>
            
            <div class="flex-1 flex justify-center">
              <!-- Logo Preview -->
              <div class="flex-shrink-0">
                <div v-if="logoPreview" class="w-[148px] h-[148px] rounded-full overflow-hidden">
                  <img :src="logoPreview" alt="Logo preview" class="w-full h-full object-cover" />
                </div>
                <div v-else class="w-[148px] h-[148px] rounded-full overflow-hidden bg-white border-2 border-gray-200 flex items-center justify-center">
                  <img src="@/assets/projects/default_logo.png" alt="Default logo" class="w-full h-full object-contain" />
                </div>
              </div>
            </div>
            
            <!-- Upload Area -->
            <div class="flex justify-end">
              <div 
                @click="triggerLogoUpload"
                @dragover.prevent
                @drop.prevent="handleLogoDrop"
                class="border-2 border-dashed border-gray-300 rounded-lg w-[152px] h-[148px] flex flex-col items-center justify-center hover:border-gray-400 cursor-pointer transition-colors"
              >
                <div class="text-center px-2">
                  <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <div class="mb-1">
                    <span class="text-xs font-medium text-gray-900">Click to upload</span>
                    <span class="text-xs text-gray-500"> or drag</span>
                  </div>
                  <p class="text-xs text-gray-500">
                    SVG, PNG, JPG, GIF (Max 3MB)
                  </p>
                </div>
              </div>
              <input
                ref="logoInput"
                type="file"
                class="hidden"
                accept=".svg,.png,.jpg,.jpeg,.gif"
                @change="handleLogoUpload"
              >
            </div>
          </div>
        </div>

        <!-- Project Cover Image Section -->
        <div class="space-y-2"> <!-- Adjusted padding between components -->
          <div class="flex items-start justify-between">
            <!-- Label and Description -->
            <div>
              <h3 class="text-sm font-medium text-gray-700 mb-1">Project Cover Image</h3>
              <p class="text-sm text-gray-500 max-w-[150px]">This will be displayed on the project public page.</p> <!-- Increased width -->
            </div>
            
            <!-- Upload Area with Preview -->
            <div class="flex-1 flex justify-end">
              <div class="w-[360px]">
                <!-- Cover Image Preview -->
                <div class="w-[320px] h-[120px] rounded-t-lg relative overflow-hidden bg-gray-100">
                  <div v-if="coverPreview" class="w-full h-full">
                    <img :src="coverPreview" alt="Cover preview" class="w-full h-full object-cover" />
                  </div>
                  <div v-else class="w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 relative flex items-center justify-center">
                    <img src="@/assets/projects/default_cover_image.png" alt="Default cover" class="w-full h-full object-cover opacity-50" />
                  </div>
                </div>
                
                <!-- Upload Area -->
                <div 
                  @click="triggerCoverUpload"
                  @dragover.prevent
                  @drop.prevent="handleCoverDrop"
                  class="border-2 border-dashed border-gray-300 rounded-b-lg w-[320px] h-[141px] flex flex-col items-center justify-center hover:border-gray-400 cursor-pointer transition-colors border-t-0"
                >
                  <div class="text-center px-2">
                    <svg class="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <div class="mb-2">
                      <span class="text-sm font-medium text-gray-900">Click to upload</span>
                      <span class="text-sm text-gray-500"> or drag</span>
                    </div>
                    <p class="text-xs text-gray-500">
                      SVG, PNG, JPG or GIF (Max 3MB)
                    </p>
                  </div>
                </div>
              </div>
              <input
                ref="coverInput"
                type="file"
                class="hidden"
                accept=".svg,.png,.jpg,.jpeg,.gif"
                @change="handleCoverUpload"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
        <button 
          @click="closeModal"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button 
          @click="handleSave"
          :disabled="loading"
          class="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
        >
          <span v-if="loading" class="loader mr-2"></span>
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios'

export default {
  name: 'ProjectSettingsModal',
  components: {},
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    project: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      visibility: this.project?.visibility || 'public',
      logoFile: null,
      logoPreview: this.project?.logo || null,
      coverFile: null,
      coverPreview: this.project?.cover_image || null,
    }
  },
  watch: {
    showModal(newValue) {
      console.log('Modal visibility changed:', this.project)
      if (newValue && this.projectId) {
        // this.fetchProjectSettings()
      }
    }
  },
  methods: {
    // async fetchProjectSettings() {
    //   if (!this.projectId) return
      
    //   this.loading = true
    //   try {
    //     const response = await apiClient.get(`/web/projects/${this.projectId}`)
    //     this.project = response.data.data
    //     this.visibility = this.project.visibility || 'public'
        
    //     console.log('Project settings loaded:', this.project)
    //     // If the project has logo/cover URLs, set them as previews
    //     if (this.project.logo_url) {
    //       this.logoPreview = this.project.logo_url
    //     }
    //     if (this.project.cover_image_url) {
    //       this.coverPreview = this.project.cover_image_url
    //     }
    //   } catch (error) {
    //     this.errorMessage = 'Failed to load project settings'
    //     console.error('Error fetching project settings:', error)
    //   } finally {
    //     this.loading = false
    //   }
    // },
    
    triggerLogoUpload() {
      this.$refs.logoInput.click()
    },

    triggerCoverUpload() {
      this.$refs.coverInput.click()
    },

    handleLogoUpload(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file size (5MB limit)
        const maxSize = 3 * 1024 * 1024; // 3MB in bytes
        if (file.size > maxSize) {
          this.$toast.error("Logo file is too large. Please select an image smaller than 3MB.");
          return;
        }

        // Validate file type
        const allowedTypes = [
          "image/svg+xml",
          "image/png",
          "image/jpeg",
          "image/jpg",
          "image/gif",
        ];
        if (!allowedTypes.includes(file.type)) {
          this.$toast.error("Invalid file type. Please select a valid image file (SVG, PNG, JPG, or GIF).");
          return;
        }

        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
        console.log("Logo uploaded:", file.name);
      }
    },

    handleCoverUpload(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file size (5MB limit)
        const maxSize = 3 * 1024 * 1024; // 3MB in bytes
        if (file.size > maxSize) {
          this.$toast.error("Cover image file is too large. Please select an image smaller than 3MB.");
          return;
        }

        // Validate file type
        const allowedTypes = [
          "image/svg+xml",
          "image/png",
          "image/jpeg",
          "image/jpg",
          "image/gif",
        ];
        if (!allowedTypes.includes(file.type)) {
          this.$toast.error("Invalid file type. Please select a valid image file (SVG, PNG, JPG, or GIF).");
          return;
        }

        this.coverFile = file;
        this.coverPreview = URL.createObjectURL(file);
        console.log("Cover image uploaded:", file.name);
      }
    },

    handleLogoDrop(event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0];

        // Validate file size (5MB limit)
        const maxSize = 3 * 1024 * 1024; // 3MB in bytes
        if (file.size > maxSize) {
          this.$toast.error("Logo file is too large. Please select an image smaller than 3MB.");
          return;
        }

        // Validate file type
        const allowedTypes = [
          "image/svg+xml",
          "image/png",
          "image/jpeg",
          "image/jpg",
          "image/gif",
        ];
        if (!allowedTypes.includes(file.type)) {
          this.$toast.error("Invalid file type. Please select a valid image file (SVG, PNG, JPG, or GIF).");
          return;
        }

        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
        console.log("Logo dropped:", file.name);
      }
    },

    handleCoverDrop(event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0];

        // Validate file size (5MB limit)
        const maxSize = 3 * 1024 * 1024; // 3MB in bytes
        if (file.size > maxSize) {
          this.$toast.error("Cover image file is too large. Please select an image smaller than 3MB.");
          return;
        }

        // Validate file type
        const allowedTypes = [
          "image/svg+xml",
          "image/png",
          "image/jpeg",
          "image/jpg",
          "image/gif",
        ];
        if (!allowedTypes.includes(file.type)) {
          this.$toast.error("Invalid file type. Please select a valid image file (SVG, PNG, JPG, or GIF).");
          return;
        }

        this.coverFile = file;
        this.coverPreview = URL.createObjectURL(file);
        console.log("Cover image dropped:", file.name);
      }
    },

    closeModal() {
      // Clean up object URLs
      if (this.logoPreview && this.logoFile) {
        URL.revokeObjectURL(this.logoPreview)
      }
      if (this.coverPreview && this.coverFile) {
        URL.revokeObjectURL(this.coverPreview)
      }
      
      this.resetForm()
      this.$emit('close')
    },
    
    resetForm() {
      this.logoFile = null
      this.coverFile = null
      // Only reset previews if they're from object URLs, not server URLs
      if (this.logoPreview && !this.logoPreview.startsWith('http')) {
        this.logoPreview = null
      }
      if (this.coverPreview && !this.coverPreview.startsWith('http')) {
        this.coverPreview = null
      }
    },

    async handleSave() {
      this.loading = true
      
      try {
        // Create a FormData object to handle file uploads
        const formData = new FormData()
        
        // Add visibility setting
        formData.append('visibility', this.visibility)
        
        // Add logo and cover files if they exist
        if (this.logoFile) {
          formData.append('logo', this.logoFile)
        }
        
        if (this.coverFile) {
          formData.append('cover_image', this.coverFile)
        }
        
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
        
        // Submit the form data
        await apiClient.patch(`/web/projects/${this.project.project_id}/settings`, formData, config)
        
        this.$toast.success('Project settings updated successfully')
        this.$emit('refresh')
        
        // Close modal after short delay to show success message
        setTimeout(() => {
          this.closeModal()
        }, 1500)
        
      } catch (error) {
        console.error("Error updating project settings:", error);

        // Handle specific error cases
        if (error.code === "ERR_NETWORK") {
          this.$toast.error("Network error. Please check your connection and try again.");
        } else if (error.response?.status === 413) {
          this.$toast.error("File too large. Please select images smaller than 3MB and try again.");
        } else if (error.response?.status === 0) {
          this.$toast.error("CORS error. Please contact support if this persists.");
        } else if (error.response?.data?.message) {
          this.$toast.error(error.response.data.message);
        } else {
          this.$toast.error("Failed to update project settings. Please try again.");
        }
      } finally {
        this.loading = false
      }
    }
  },
  beforeUnmount() {
    // Clean up any created object URLs when component is unmounted
    if (this.logoPreview && this.logoFile) {
      URL.revokeObjectURL(this.logoPreview)
    }
    if (this.coverPreview && this.coverFile) {
      URL.revokeObjectURL(this.coverPreview)
    }
  }
}
</script>

<style scoped>
.modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 50;
}

.modal-content {
  position: relative;
}

.bg-gradient-to-r {
  background: linear-gradient(to right, #fbbf24, #f59e0b);
}

.loader {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>